import { Articolo } from '@/types';

/**
 * Controlli di magazzino per le vendite (movimenti di ENTRATA).
 *
 * Il backend rifiuta la registrazione quando la giacenza andrebbe sotto zero
 * (`Giacenza insufficiente per l'articolo ...`). Un ordine viene però salvato una
 * riga alla volta: senza un controllo a monte le righe precedenti restano scritte
 * e il movimento non si chiude mai. Qui la copertura del magazzino viene calcolata
 * sull'intero ordine, prima di toccare il database.
 */

/** Articolo dell'ordine che il magazzino non riesce a coprire */
export interface CarenzaMagazzino {
  articoloId: number;
  nome: string;
  codice?: string;
  richiesto: number;
  disponibile: number;
}

/** Riga d'ordine, nella forma minima che serve ai controlli di magazzino */
export interface RigaOrdineMagazzino {
  articoloId?: number;
  quantita: string;
  /** Valorizzato solo per le righe già salvate (modifica di un ordine esistente) */
  movimentoId?: number;
  /** Quantità già scaricata dal magazzino da questa riga */
  quantitaOriginale?: number;
}

/** Movimento in modifica, nella forma minima che serve ai controlli di magazzino */
export interface MovimentoInModifica {
  tipo: 'entrata' | 'uscita';
  articolo_id?: number;
  quantita?: number;
}

export interface ContestoMagazzino {
  articoli: Articolo[];
  righe: RigaOrdineMagazzino[];
  movimentoInModifica?: MovimentoInModifica | null;
}

/**
 * Merce che può essere venduta per un articolo.
 *
 * In modifica la quantità già registrata dalla riga è stata scaricata dal magazzino:
 * il backend la ripristina prima di riapplicare quella nuova, quindi va riaccreditata
 * qui, altrimenti riaprire un ordine già salvato risulterebbe sempre in carenza.
 */
export function disponibilitaArticolo(articoloId: number, ctx: ContestoMagazzino): number {
  const articolo = ctx.articoli.find(a => a.id === articoloId);
  if (!articolo) return 0;

  let giaScaricata = ctx.righe
    .filter(r => r.articoloId === articoloId && r.movimentoId)
    .reduce((sum, r) => sum + (r.quantitaOriginale ?? 0), 0);

  // Movimento singolo in modifica (nessuna riga caricata): stessa logica
  const inModifica = ctx.movimentoInModifica;
  if (ctx.righe.length === 0 && inModifica?.tipo === 'entrata' && inModifica.articolo_id === articoloId) {
    giaScaricata += inModifica.quantita ?? 0;
  }

  return (articolo.quantita ?? 0) + giaScaricata;
}

/**
 * Articoli richiesti in quantità superiore alla giacenza.
 *
 * Il controllo è per articolo e non per riga: lo stesso codice può comparire più
 * volte nell'ordine e sono le quantità complessive a dover stare dentro la giacenza.
 * Gli acquisti (USCITA) e i giroconti rientrano il magazzino, quindi non hanno vincoli.
 */
export function calcolaCarenzeMagazzino(params: {
  tipoMovimento: 'entrata' | 'uscita';
  isGiroconto: boolean;
  articoli: Articolo[];
  righe: RigaOrdineMagazzino[];
  /** Articolo del form a riga singola (usato solo quando non ci sono righe) */
  articoloSingoloId?: number;
  quantitaSingola?: string;
  movimentoInModifica?: MovimentoInModifica | null;
}): CarenzaMagazzino[] {
  const { tipoMovimento, isGiroconto, articoli, righe, articoloSingoloId, quantitaSingola, movimentoInModifica } = params;
  if (isGiroconto || tipoMovimento !== 'entrata') return [];

  const richiestoPerArticolo = new Map<number, number>();
  if (righe.length > 0) {
    for (const riga of righe) {
      if (!riga.articoloId) continue;
      const qta = parseFloat(riga.quantita) || 0;
      richiestoPerArticolo.set(riga.articoloId, (richiestoPerArticolo.get(riga.articoloId) ?? 0) + qta);
    }
  } else if (articoloSingoloId) {
    richiestoPerArticolo.set(articoloSingoloId, parseFloat(quantitaSingola ?? '') || 0);
  }

  const ctx: ContestoMagazzino = { articoli, righe, movimentoInModifica };
  const carenze: CarenzaMagazzino[] = [];
  richiestoPerArticolo.forEach((richiesto, articoloId) => {
    if (richiesto <= 0) return;
    const disponibile = disponibilitaArticolo(articoloId, ctx);
    if (richiesto > disponibile) {
      const articolo = articoli.find(a => a.id === articoloId);
      carenze.push({
        articoloId,
        nome: articolo?.nome ?? 'Articolo',
        codice: articolo?.codice,
        richiesto,
        disponibile,
      });
    }
  });
  return carenze;
}

/** Testo dell'avviso: distingue l'articolo finito da quello solo insufficiente. */
export function descriviCarenza(c: CarenzaMagazzino): string {
  const etichetta = c.codice ? `${c.nome} [${c.codice}]` : c.nome;
  return c.disponibile <= 0
    ? `"${etichetta}" è esaurito (giacenza 0): rifornirlo prima di venderlo`
    : `"${etichetta}": richiesti ${c.richiesto}, disponibili solo ${c.disponibile}`;
}
